// src/Dashboard/pages/teacher/PrepaPhysique/components/ExerciseDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaDumbbell,
  FaExternalLinkAlt,
  FaImage,
  FaLink,
  FaSave,
  FaTimes,
  FaVideo,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const initialForm = {
  nom: "",
  programme: "",
  description: "",
  lien_photo: "",
  lien_video: "",
};

const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export default function ExerciseDrawer({
  isOpen,
  mode = "create",
  exercise,
  saving = false,
  onClose,
  onCreate,
  onUpdate,
}) {
  const formRef = useRef(null);

  const [formValues, setFormValues] = useState(initialForm);
  const [imgOk, setImgOk] = useState(true);

  const isCreate = mode === "create";
  const isEdit = mode === "edit";
  const isDetails = mode === "details";
  const editable = isCreate || isEdit;

  const title = useMemo(() => {
    if (isCreate) return "Ajouter un exercice";
    if (isEdit) return "Modifier l’exercice";
    return "Détail de l’exercice";
  }, [isCreate, isEdit]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    if (isCreate) {
      setFormValues(initialForm);
      setImgOk(true);
      return;
    }

    setFormValues({
      nom: exercise?.nom || "",
      programme: exercise?.programme || "",
      description: exercise?.description || "",
      lien_photo: exercise?.lien_photo || "",
      lien_video: exercise?.lien_video || "",
    });

    setImgOk(true);
  }, [isOpen, isCreate, exercise]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormValues((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!editable) return;

    if (isCreate) {
      onCreate?.(formValues);
      return;
    }

    onUpdate?.(formValues);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Préparation physique
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {isDetails ? (
              <ExerciseDetails exercise={exercise} imgOk={imgOk} setImgOk={setImgOk} />
            ) : (
              <div className="space-y-5">
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaDumbbell />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Informations principales
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Nom de l’exercice, programme et contenu détaillé.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                      label="Nom de l’exercice *"
                      name="nom"
                      value={formValues.nom}
                      onChange={handleChange}
                      required
                      placeholder="Ex : Gainage frontal"
                    />

                    <Field
                      label="Programme"
                      name="programme"
                      value={formValues.programme}
                      onChange={handleChange}
                      placeholder="Ex : Renforcement, cardio, mobilité..."
                    />
                  </div>

                  <div className="mt-4">
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Description
                    </label>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                      <ReactQuill
                        theme="snow"
                        value={formValues.description}
                        onChange={(value) =>
                          setFormValues((previous) => ({
                            ...previous,
                            description: value,
                          }))
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Décrivez les consignes, séries, temps de repos, variantes..."
                        className="bg-white dark:bg-slate-950"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <FaLink />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Médias
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Image d’illustration et vidéo explicative.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                      label="URL de l’image"
                      name="lien_photo"
                      value={formValues.lien_photo}
                      onChange={(event) => {
                        setImgOk(true);
                        handleChange(event);
                      }}
                      placeholder="https://..."
                      icon={<FaImage />}
                    />

                    <Field
                      label="URL de la vidéo"
                      name="lien_video"
                      value={formValues.lien_video}
                      onChange={handleChange}
                      placeholder="https://..."
                      icon={<FaVideo />}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Aperçu image
                      </p>

                      {formValues.lien_photo && imgOk ? (
                        <img
                          src={formValues.lien_photo}
                          alt="Aperçu"
                          onError={() => setImgOk(false)}
                          className="h-48 w-full rounded-xl object-contain bg-white dark:bg-slate-950"
                        />
                      ) : (
                        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                          Aucune image valide.
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Lien vidéo
                      </p>

                      {formValues.lien_video ? (
                        <a
                          href={formValues.lien_video}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 ring-1 ring-blue-200 transition hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60"
                        >
                          Ouvrir la vidéo
                          <FaExternalLinkAlt />
                        </a>
                      ) : (
                        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                          Aucune vidéo renseignée.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                {isDetails ? "Fermer" : "Annuler"}
              </button>

              {editable && (
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              )}
            </div>
          </div>
        </form>
      </aside>
    </div>,
    document.body
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  required,
  icon,
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        )}

        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={`w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white ${
            icon ? "pl-9" : ""
          }`}
        />
      </div>
    </div>
  );
}

function ExerciseDetails({ exercise, imgOk, setImgOk }) {
  return (
    <div className="space-y-5">
      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            <FaDumbbell />
          </span>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Exercice
            </p>

            <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {exercise?.nom || "—"}
            </h3>

            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {exercise?.programme || "Sans programme"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <h4 className="mb-3 flex items-center gap-2 font-bold text-slate-900 dark:text-white">
            <FaImage className="text-indigo-500" />
            Illustration
          </h4>

          {exercise?.lien_photo && imgOk ? (
            <img
              src={exercise.lien_photo}
              alt={exercise?.nom || "Illustration"}
              onError={() => setImgOk(false)}
              className="max-h-[360px] w-full rounded-2xl object-contain bg-slate-50 dark:bg-slate-900"
            />
          ) : (
            <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Aucune image.
            </div>
          )}
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <h4 className="mb-3 flex items-center gap-2 font-bold text-slate-900 dark:text-white">
            <FaVideo className="text-indigo-500" />
            Vidéo
          </h4>

          {exercise?.lien_video ? (
            <a
              href={exercise.lien_video}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 ring-1 ring-blue-200 transition hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60"
            >
              Ouvrir la vidéo
              <FaExternalLinkAlt />
            </a>
          ) : (
            <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Aucune vidéo.
            </div>
          )}
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
        <h4 className="mb-3 font-bold text-slate-900 dark:text-white">
          Description
        </h4>

        {exercise?.description ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: exercise.description }}
          />
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Aucune description.
          </p>
        )}
      </div>
    </div>
  );
}